import * as React from 'react';
import { createContext } from 'react';
import { DocumentType } from './constants';
import { useApplicationContext } from '../core/contexts/ApplicationContext.tsx';
import { SwitchSupportModal, useSupportMode } from '../support/components/SupportBar.tsx';
import {
    canManageAllDocumentTypes,
    canManageChannels,
    canManageMasterData,
    canSeeTestingDocuments,
} from '../users/utils';
import { IRouteProps } from '../core/utils/modules';
import type { IUser } from '../users/interfaces';
import MasterDataImportLog from '../masterdata/components/MasterDataImportLog.tsx';
import { ChannelContext, useChannelStore } from './stores';
import Layout from '../core/components/Layout';
import { useParams } from 'react-router-dom';
import { flatten } from 'lodash';
import { IChannel } from './stores/channelStore.tsx';

interface RouteFactoryProps {
    paths: RouteFactoryPathProps;
    pageComponents: RouteFactoryPageComponentProps;
    documentType: DocumentType;
    documentTypeNavTabKey: string;
    documentTypeEnabled: (customer: any) => boolean;
}

interface RouteFactoryPathProps {
    allDocuments: string;
    allDocumentsFinished: string;
    allDocumentsTesting: string;
    channelDocuments: string;
    channelDocumentsFinished: string;
    channelDocumentsTesting: string;
    assistance: string;
    assistanceTab: string;
    channelConfig: string;
    channelMasterdata: string;
}

interface RouteFactoryPageComponentProps {
    Overview: any;
    Configuration: any;
    Assistance: any;
}

interface INavigationContext {
    paths?: RouteFactoryPathProps;
    channelId?: string;
    recordId?: string;
}

export const useChannels = (documentType: DocumentType) => {
    const { channelId } = useParams();
    const channelStore = useChannelStore();
    const activeChannelId = channelStore.channelId || channelId;

    const { data, error, loading } =
        documentType != null
            ? channelStore.getDocumentChannels(documentType)
            : { data: {}, error: null, loading: false };

    const channels = loading || !data ? [] : (flatten(Object.values(data)) as IChannel[]);
    const activeChannel = channels.find((channel) => channel.id === activeChannelId);

    return { channels, activeChannel, loading, error };
};

const ChannelSupportModal = ({ documentType }: { documentType: DocumentType }) => {
    const { channelId } = useParams();
    const { user } = useApplicationContext();
    const { isSessionUserOutOfSync } = useSupportMode(user);
    const { channels, loading, error } = useChannels(documentType);

    const isChannelFromOtherCustomer =
        Boolean(channelId) && !loading && !error && !channels.some((channel) => channel.id === channelId);

    return (
        <SwitchSupportModal
            canSwitch={isChannelFromOtherCustomer || isSessionUserOutOfSync}
            channelId={channelId}
            documentType={documentType}
        />
    );
};

export const NavigationContext = createContext<INavigationContext>({ paths: null, channelId: null, recordId: null });

export const buildRoutes = ({
    paths,
    pageComponents,
    documentType,
    documentTypeNavTabKey,
    documentTypeEnabled,
}: RouteFactoryProps): Array<IRouteProps> => {
    const { Overview, Configuration, Assistance } = pageComponents;
    const canAccessDocumentType = (user: IUser) =>
        (user?.customer && documentTypeEnabled(user.customer)) || canManageAllDocumentTypes(user);
    const assistancePaths = [paths.assistance, paths.assistanceTab];

    return [
        {
            path: paths.allDocuments,
            component: (props) => {
                const { activeChannel } = useChannels(documentType);
                return (
                    <Layout>
                        <ChannelContext.Provider value={{ activeChannel, documentType }}>
                            <Overview finished={false} {...props} />
                        </ChannelContext.Provider>
                    </Layout>
                );
            },
            condition: ({ user }) => canAccessDocumentType(user),
        },
        {
            path: paths.allDocumentsFinished,
            component: (props) => {
                const { activeChannel } = useChannels(documentType);
                return (
                    <Layout>
                        <ChannelContext.Provider value={{ activeChannel, documentType }}>
                            <Overview finished={true} {...props} />
                        </ChannelContext.Provider>
                    </Layout>
                );
            },
            condition: ({ user }) => canAccessDocumentType(user),
        },
        {
            path: paths.channelDocuments,
            component: (props) => {
                const { activeChannel } = useChannels(documentType);
                return (
                    <Layout>
                        <ChannelContext.Provider value={{ activeChannel, documentType }}>
                            <Overview finished={false} {...props} key={props?.params?.channelId} />
                        </ChannelContext.Provider>
                    </Layout>
                );
            },
            condition: ({ user }) => canAccessDocumentType(user),
        },
        {
            path: paths.channelDocumentsFinished,
            component: (props) => {
                const { activeChannel } = useChannels(documentType);
                return (
                    <Layout>
                        <ChannelContext.Provider value={{ activeChannel, documentType }}>
                            <Overview finished={true} {...props} key={props?.params?.channelId} />
                        </ChannelContext.Provider>
                    </Layout>
                );
            },
            condition: ({ user }) => canAccessDocumentType(user),
        },
        {
            path: paths.allDocumentsTesting,
            component: (props) => {
                const { activeChannel } = useChannels(documentType);
                return (
                    <Layout>
                        <ChannelContext.Provider value={{ activeChannel, documentType }}>
                            <Overview showTestingDocuments={true} {...props} />
                        </ChannelContext.Provider>
                    </Layout>
                );
            },
            condition: ({ user }) => canSeeTestingDocuments(user),
        },
        {
            path: paths.channelDocumentsTesting,
            component: (props) => {
                const { activeChannel } = useChannels(documentType);
                return (
                    <Layout>
                        <ChannelContext.Provider value={{ activeChannel, documentType }}>
                            <Overview showTestingDocuments={true} {...props} key={props?.params?.channelId} />
                        </ChannelContext.Provider>
                    </Layout>
                );
            },
            condition: ({ user }) => canSeeTestingDocuments(user),
        },
        {
            path: paths.channelConfig,
            component: (props) => {
                const { activeChannel } = useChannels(documentType);
                return (
                    <ChannelContext.Provider value={{ activeChannel, documentType }}>
                        <Configuration
                            channelPath={paths.channelDocuments}
                            configPath={paths.channelConfig}
                            {...props}
                        />
                    </ChannelContext.Provider>
                );
            },
            condition: ({ user }) => canAccessDocumentType(user) && canManageChannels(user),
        },
        {
            path: paths.channelMasterdata,
            component: (props) => {
                const { activeChannel } = useChannels(documentType);
                return (
                    <ChannelContext.Provider value={{ activeChannel, documentType }}>
                        <MasterDataImportLog channelPath={paths.channelDocuments} {...props} />
                    </ChannelContext.Provider>
                );
            },
            condition: ({ user }) => canAccessDocumentType(user) && canManageMasterData(user),
        },
        {
            path: paths.assistance,
            component: (props) => <Assistance key={props?.params?.recordId} {...props} />,
            condition: ({ user }) => canAccessDocumentType(user),
        },
        {
            path: paths.assistanceTab,
            component: (props) => <Assistance key={props?.params?.recordId} {...props} />,
            condition: ({ user }) => canAccessDocumentType(user),
        },
    ].map(({ component: WrappedComponent, ...rest }) => ({
        ...rest,
        // Inject navigation context for each page
        component: (props) => {
            return (
                <NavigationContext.Provider value={{ paths }}>
                    {!assistancePaths.includes(rest.path) && <ChannelSupportModal documentType={documentType} />}
                    <WrappedComponent {...props} />
                </NavigationContext.Provider>
            );
        },
    }));
};
